import React, { useState, useRef } from 'react';
import { StyleSheet, Text, View, TextInput, TouchableOpacity, ActivityIndicator, ScrollView, KeyboardAvoidingView, Platform, Image } from 'react-native';
import { createUserWithEmailAndPassword } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { auth, firestore } from '../../services/firebase';
import { NativeStackScreenProps } from '@react-navigation/native-stack';
import { AuthStackParamList } from '../../navigation/AuthNavigator';
import { User } from '../../types';
import { appConfig } from '../../config/appConfig';

type Props = NativeStackScreenProps<AuthStackParamList, 'Register'>;

const RegisterScreen = ({ navigation }: Props) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  
  const lastNameRef = useRef<TextInput>(null);
  const usernameRef = useRef<TextInput>(null);
  const emailRef = useRef<TextInput>(null);
  const passwordRef = useRef<TextInput>(null);
  const confirmPasswordRef = useRef<TextInput>(null);

  const generateFriendCode = (): string => {
    return Math.random().toString(36).substring(2, 10).toUpperCase();
  };

  const handleKeyPress = (e: React.KeyboardEvent | any) => {
    if (e.nativeEvent && e.nativeEvent.key === 'Enter') {
      handleRegister();
    }
  };

  const handleRegister = async () => {
    // Input validation
    if (!firstName || !lastName || !username || !email || !password || !confirmPassword) {
      setErrorMessage('Por favor completa todos los campos');
      return;
    }

    if (password !== confirmPassword) {
      setErrorMessage('Las contraseñas no coinciden');
      return;
    }

    if (!acceptTerms) {
      setErrorMessage('Debes aceptar los términos y condiciones');
      return;
    }

    setLoading(true);
    setErrorMessage(null);
    try {
      // Create user in Firebase Auth
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const uid = userCredential.user.uid;
      
      // Generate friend code
      const friendCode = generateFriendCode();
      
      // Create user document in Firestore
      const userData: User = {
        id: uid,
        firstName,
        lastName,
        username,
        email,
        friendCode,
        friends: [],
        acceptedTerms: true,
        acceptedTermsDate: new Date().toISOString(),
        pendingFriendRequests: [],
        friendRequests: []
      };
      
      await setDoc(doc(firestore, 'users', uid), userData);
      
      // After successful registration, user will be automatically signed in
      // and the auth state listener will redirect to main app
    } catch (error: any) {
      let message = 'Error desconocido al registrarse';
      
      // Mapear códigos de error comunes de Firebase Auth
      if (error.code) {
        switch (error.code) {
          case 'auth/email-already-in-use':
            message = 'Este correo electrónico ya está en uso.';
            break;
          case 'auth/invalid-email':
            message = 'El formato del correo electrónico es inválido.';
            break;
          case 'auth/weak-password':
            message = 'La contraseña es demasiado débil. Debe tener al menos 6 caracteres.';
            break;
          case 'auth/network-request-failed':
            message = 'Error de conexión. Verifica tu conexión a internet.';
            break;
          default:
            message = `Error de registro: ${error.message}`;
        }
      }
      
      setErrorMessage(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === "ios" ? "padding" : undefined}
      style={{ flex: 1 }}
    >
      <ScrollView style={styles.scrollView}>
        <View style={styles.container}>
          <View style={styles.logoContainer}>
            <Image 
              source={require('../../../assets/logo-1024.png')} 
              style={styles.logo} 
              resizeMode="contain"
            />
          </View>
          
          <Text style={styles.title}>Crear Cuenta</Text>
          
          <View style={styles.inputContainer}>
            <TextInput
              style={styles.input}
              placeholder="Nombre"
              value={firstName}
              onChangeText={(text) => {
                setFirstName(text);
                setErrorMessage(null);
              }}
              returnKeyType="next"
              onSubmitEditing={() => lastNameRef.current?.focus()}
              blurOnSubmit={false}
              onKeyPress={handleKeyPress}
            />
            
            <TextInput
              ref={lastNameRef}
              style={styles.input}
              placeholder="Apellido"
              value={lastName}
              onChangeText={(text) => {
                setLastName(text);
                setErrorMessage(null);
              }}
              returnKeyType="next"
              onSubmitEditing={() => usernameRef.current?.focus()}
              blurOnSubmit={false}
              onKeyPress={handleKeyPress}
            />
            
            <TextInput
              ref={usernameRef}
              style={styles.input}
              placeholder="Nombre de usuario"
              value={username}
              onChangeText={(text) => {
                setUsername(text);
                setErrorMessage(null);
              }}
              autoCapitalize="none"
              returnKeyType="next"
              onSubmitEditing={() => emailRef.current?.focus()}
              blurOnSubmit={false}
              onKeyPress={handleKeyPress}
            />
            
            <TextInput
              ref={emailRef}
              style={styles.input}
              placeholder="Correo electrónico"
              value={email}
              onChangeText={(text) => {
                setEmail(text);
                setErrorMessage(null);
              }}
              keyboardType="email-address"
              autoCapitalize="none"
              returnKeyType="next"
              onSubmitEditing={() => passwordRef.current?.focus()}
              blurOnSubmit={false}
              onKeyPress={handleKeyPress}
            />
            
            <TextInput
              ref={passwordRef}
              style={styles.input}
              placeholder="Contraseña"
              value={password}
              onChangeText={(text) => {
                setPassword(text);
                setErrorMessage(null);
              }}
              secureTextEntry
              returnKeyType="next"
              onSubmitEditing={() => confirmPasswordRef.current?.focus()}
              blurOnSubmit={false}
              onKeyPress={handleKeyPress}
            />
            
            <TextInput
              ref={confirmPasswordRef}
              style={styles.input}
              placeholder="Confirmar contraseña"
              value={confirmPassword}
              onChangeText={(text) => {
                setConfirmPassword(text);
                setErrorMessage(null);
              }}
              secureTextEntry
              returnKeyType="go"
              onSubmitEditing={handleRegister}
              onKeyPress={handleKeyPress}
            />
          </View>

          <TouchableOpacity 
            style={styles.termsContainer}
            onPress={() => {
              setAcceptTerms(!acceptTerms);
              setErrorMessage(null);
            }}
          >
            <View style={[styles.checkbox, acceptTerms && styles.checkboxChecked]} />
            <Text style={styles.termsText}>
              Acepto los 
              <Text 
                style={styles.termsLink}
                onPress={(e) => {
                  e.stopPropagation();
                  navigation.navigate('TermsConditions');
                }}
              > términos y condiciones</Text>
              <Text> y la </Text>
              <Text 
                style={styles.termsLink}
                onPress={(e) => {
                  e.stopPropagation();
                  navigation.navigate('PrivacyPolicy');
                }}
              >política de privacidad</Text>
            </Text>
          </TouchableOpacity>

          {errorMessage ? (
            <View style={styles.errorContainer}>
              <Text style={styles.errorText}>{errorMessage}</Text>
            </View>
          ) : null}

          <TouchableOpacity 
            style={styles.button} 
            onPress={handleRegister}
            disabled={loading}
            accessibilityRole="button"
          >
            {loading ? (
              <ActivityIndicator color="#fff" />
            ) : (
              <Text style={styles.buttonText}>Registrarse</Text>
            )}
          </TouchableOpacity>

          <TouchableOpacity 
            style={styles.linkButton}
            onPress={() => navigation.navigate('Login')}
          >
            <Text style={styles.linkText}>¿Ya tienes una cuenta? Inicia sesión</Text>
          </TouchableOpacity>
        </View>
      </ScrollView>
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  scrollView: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  container: {
    flex: 1,
    padding: 20,
    justifyContent: 'center',
  },
  logoContainer: {
    alignItems: 'center',
    marginTop: 30,
  },
  logo: {
    width: 80,
    height: 80,
    borderRadius: 20,
  },
  title: {
    fontSize: 28,
    fontWeight: 'bold',
    textAlign: 'center',
    marginBottom: 30,
    marginTop: 10,
    color: '#FF4500',
  },
  inputContainer: {
    marginBottom: 20,
  },
  input: {
    backgroundColor: 'white',
    padding: 15,
    borderRadius: 10,
    marginBottom: 15,
    borderWidth: 1,
    borderColor: '#ddd',
  },
  termsContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 20,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderRadius: 4,
    borderWidth: 1,
    borderColor: '#FF4500',
    marginRight: 10,
  },
  checkboxChecked: {
    backgroundColor: '#FF4500',
  },
  termsText: {
    color: '#555',
    flex: 1,
  },
  termsLink: {
    color: '#FF4500',
    textDecorationLine: 'underline',
  },
  button: {
    backgroundColor: '#FF4500',
    padding: 15,
    borderRadius: 10,
    alignItems: 'center',
  },
  buttonText: {
    color: 'white',
    fontWeight: 'bold',
    fontSize: 16,
  },
  linkButton: {
    marginTop: 15,
    alignItems: 'center',
    marginBottom: 40,
  },
  linkText: {
    color: '#FF4500',
    fontSize: 14,
  },
  errorContainer: {
    marginBottom: 15,
    backgroundColor: '#FFEBEE',
    padding: 10,
    borderRadius: 5,
    borderWidth: 1,
    borderColor: '#FFCDD2',
  },
  errorText: {
    color: '#D32F2F',
    textAlign: 'center',
    fontSize: 14,
  },
});

export default RegisterScreen; 